import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Download, Settings } from 'lucide-react'
import { Button, Card } from '@/components/ui'
import { PeriodSelector } from '@/components/analytics/PeriodSelector'
import {
  ShmpCoursesCard,
  ShmpForecastCard,
  ShmpFunnelCard,
  ShmpPlanFactCard,
  type ShmpPlanFactMode,
  ShmpRankingCard,
} from '@/components/shmp/ShmpDashboardCards'
import { ShmpCourseWeeklyPlanModal } from '@/components/shmp/ShmpCourseWeeklyPlanModal'
import {
  ShmpPartnersTable,
  type ShmpSortKey,
} from '@/components/shmp/ShmpPartnersTable'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import { useShmpCourseWeeklyPlan } from '@/hooks/useShmpCourseWeeklyPlan'
import { useShmpDashboard } from '@/hooks/useShmpDashboard'
import { useShmpListokFact } from '@/hooks/useShmpListokFact'
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { exportShmpCsv, exportShmpXlsx } from '@/lib/shmpExport'
import type { ShmpCourseNumber } from '@/types'

const COURSES: ShmpCourseNumber[] = [1, 2, 3]

export function ShmpDashboardPage() {
  const navigate = useNavigate()
  const periods = useStudioPeriods()
  const timeFilter = useAnalyticsTimeFilter(periods.currentPeriod)
  const [course, setCourse] = useState<'all' | ShmpCourseNumber>('all')
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<ShmpSortKey>('revenue_fact')
  const [showExport, setShowExport] = useState(false)
  const [showManualPlanModal, setShowManualPlanModal] = useState(false)
  const [planFactMode, setPlanFactMode] = useState<ShmpPlanFactMode>('app')
  const dashboard = useShmpDashboard({
    periodId: periods.currentPeriod?.period_id ?? null,
    weekNumber: timeFilter.selectedRange?.weekNumber ?? null,
    courseNumber: course === 'all' ? null : course,
  })
  const manualPlan = useShmpCourseWeeklyPlan({
    periodId: periods.currentPeriod?.period_id ?? null,
    courseNumber: course === 'all' ? null : course,
  })
  const listokFact = useShmpListokFact({
    periodId: periods.currentPeriod?.period_id ?? null,
    weekNumber: timeFilter.selectedRange?.weekNumber ?? null,
    courseNumber: course === 'all' ? null : course,
  })

  const partners = useMemo(() => {
    const query = search.trim().toLowerCase()
    const rows = (dashboard.data?.partners ?? []).filter(row =>
      !query
      || row.partner_number.toString().includes(query)
      || row.persons_fio?.toLowerCase().includes(query)
      || row.cities_text?.toLowerCase().includes(query)
    )
    return [...rows].sort((a, b) => (b[sortKey] ?? -Infinity) - (a[sortKey] ?? -Infinity))
  }, [dashboard.data?.partners, search, sortKey])

  const rankings = useMemo(() => {
    const comparable = (dashboard.data?.partners ?? [])
      .filter(row => row.revenue_growth_pct !== null)
    return {
      top: [...comparable]
        .filter(row => (row.revenue_growth_pct ?? 0) > 0)
        .sort((a, b) => (b.revenue_growth_pct ?? 0) - (a.revenue_growth_pct ?? 0))
        .slice(0, 3),
      bottom: [...comparable]
        .filter(row => (row.revenue_growth_pct ?? 0) < 0)
        .sort((a, b) => (a.revenue_growth_pct ?? 0) - (b.revenue_growth_pct ?? 0))
        .slice(0, 3),
    }
  }, [dashboard.data?.partners])

  const summary = dashboard.data?.summary
  const forecast = useMemo(() => {
    if (!summary || !timeFilter.selectedRange) return null
    const start = new Date(`${timeFilter.selectedRange.start}T00:00:00`)
    const end = new Date(`${timeFilter.selectedRange.end}T00:00:00`)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (today < start) return null
    if (today > end) return summary.revenue_fact
    const elapsedDays = Math.floor((today.getTime() - start.getTime()) / 86_400_000) + 1
    return elapsedDays > 0
      ? summary.revenue_fact / elapsedDays * timeFilter.selectedRange.rangeDays
      : null
  }, [summary, timeFilter.selectedRange])

  const exportFilename = `shmp-${periods.currentPeriod?.period_id ?? 'period'}-${timeFilter.selectedRange?.weekNumber ?? 'all'}`
  const filteredManualPlanRows = useMemo(() => {
    if (!timeFilter.selectedRange?.weekNumber) return manualPlan.data
    return manualPlan.data.filter(
      (row) => row.week_number_in_period === timeFilter.selectedRange?.weekNumber
    )
  }, [manualPlan.data, timeFilter.selectedRange?.weekNumber])
  const manualPlanTotal = useMemo(
    () => filteredManualPlanRows.reduce((sum, row) => sum + (row.planned_revenue_rub ?? 0), 0),
    [filteredManualPlanRows]
  )

  if (periods.loading) {
    return <Card><div className="text-sm text-muted-foreground">Загрузка периодов...</div></Card>
  }

  return (
    <div className="space-y-3 pb-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">ШМП</h1>
          <p className="mt-1 text-xs text-muted-foreground">Сводная аналитика по франчайзи</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[310px] rounded-lg border border-border bg-card px-1 shadow-sm">
            <PeriodSelector
              currentPeriod={periods.currentPeriod}
              onPrevPeriod={periods.goToPrevPeriod}
              onNextPeriod={periods.goToNextPeriod}
              canGoPrev={periods.canGoPrev}
              canGoNext={periods.canGoNext}
              isCurrentPeriod={periods.isCurrentPeriod}
              showViewModeSelector
              periodFilterValue={timeFilter.selectedTimeFilter}
              periodFilterOptions={timeFilter.timeFilterOptions}
              onPeriodFilterChange={timeFilter.setSelectedTimeFilter}
              selectedRange={timeFilter.selectedRange}
              compact
            />
          </div>
          <select
            value={course}
            onChange={event => setCourse(
              event.target.value === 'all' ? 'all' : Number(event.target.value) as ShmpCourseNumber
            )}
            className="rounded-lg border border-border bg-card px-3 py-2.5 text-xs shadow-sm"
          >
            <option value="all">Все курсы</option>
            {COURSES.map(item => <option key={item} value={item}>{item} курс</option>)}
          </select>
          <div className="relative">
            <Button size="sm" variant="outline" onClick={() => setShowExport(value => !value)}>
              <Download size={15} className="mr-1.5 inline" />
              Экспорт
            </Button>
            {showExport && dashboard.data && (
              <div className="absolute right-0 z-20 mt-1 w-36 rounded-lg border border-border bg-card p-1 text-xs shadow-lg">
                <button
                  className="w-full rounded-md px-3 py-2 text-left hover:bg-muted"
                  onClick={() => {
                    void exportShmpXlsx(dashboard.data!, partners, exportFilename)
                    setShowExport(false)
                  }}
                >
                  XLSX
                </button>
                <button
                  className="w-full rounded-md px-3 py-2 text-left hover:bg-muted"
                  onClick={() => {
                    exportShmpCsv(partners, exportFilename)
                    setShowExport(false)
                  }}
                >
                  CSV
                </button>
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/management/shmp/members')}>
            <Settings size={15} className="mr-1.5 inline" />
            Состав
          </Button>
        </div>
      </div>

      {periods.error && <Card><div className="text-sm text-error">{periods.error}</div></Card>}
      {dashboard.error && <Card><div className="text-sm text-error">{dashboard.error}</div></Card>}
      {manualPlan.error && <Card><div className="text-sm text-error">{manualPlan.error}</div></Card>}
      {listokFact.error && <Card><div className="text-sm text-error">{listokFact.error}</div></Card>}
      {dashboard.loading && <Card><div className="text-sm text-muted-foreground">Загрузка аналитики...</div></Card>}
      {manualPlan.loading && <Card><div className="text-sm text-muted-foreground">Загрузка ручного плана ШМП...</div></Card>}
      {listokFact.loading && <Card><div className="text-sm text-muted-foreground">Загрузка факта ШМП по листку...</div></Card>}

      {summary && dashboard.data && (
        <>
          {summary.revenue_plan <= 0 && (
            <Card className="border border-primary/50 bg-primary/5 shadow-none">
              <div className="text-sm font-medium">За выбранный диапазон не заполнен план.</div>
            </Card>
          )}
          {summary.studio_count > 0
            && timeFilter.selectedRange
            && summary.days_with_data < timeFilter.selectedRange.rangeDays * summary.studio_count && (
              <Card className="border border-info/30 bg-info/5 shadow-none">
                <div className="text-sm font-medium">Факт заполнен частично. Прогноз будет уточняться по мере внесения данных.</div>
              </Card>
          )}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <ShmpPlanFactCard
                mode={planFactMode}
                onModeChange={setPlanFactMode}
                plan={planFactMode === 'app' ? summary.revenue_plan : manualPlanTotal}
                fact={planFactMode === 'app' ? summary.revenue_fact : listokFact.data.fact_revenue_rub}
                onEditManualPlan={() => setShowManualPlanModal(true)}
              />
            </div>
            <div className="lg:col-span-3"><ShmpForecastCard summary={summary} forecast={forecast} /></div>
            <div className="lg:col-span-5"><ShmpFunnelCard summary={summary} /></div>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <ShmpCoursesCard courses={dashboard.data.courses} summary={summary} />
            <ShmpRankingCard title="Топ франчайзи" partners={rankings.top} />
            <ShmpRankingCard title="Анти-топ франчайзи" partners={rankings.bottom} negative />
          </div>

          <ShmpPartnersTable
            partners={partners}
            summary={summary}
            search={search}
            onSearchChange={setSearch}
            sortKey={sortKey}
            onSortChange={setSortKey}
            onOpenPartner={partnerNumber => navigate(`/partners/${partnerNumber}/analytics`)}
          />
        </>
      )}

      <ShmpCourseWeeklyPlanModal
        isOpen={showManualPlanModal}
        onClose={() => setShowManualPlanModal(false)}
        periodId={periods.currentPeriod?.period_id ?? null}
        rows={manualPlan.data}
        initialWeekNumber={timeFilter.selectedRange?.weekNumber ?? null}
        onSaved={async () => {
          await manualPlan.refresh()
        }}
      />
    </div>
  )
}
